import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth, APPROVER_ROLES } from "../context/AuthContext";
import { PageHeader } from "../components/Card";
import { DataTable, FilterSelect } from "../components/DataTable";
import { StatusPill } from "../components/Severity";

export default function Approvals() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [comment, setComment] = useState("");
  const load = useCallback(() => api.get("/approvals", { params: { status } }).then((r) => setRows(r.data)), [status]);
  useEffect(() => { load(); }, [load]);

  const decide = async () => {
    try {
      await api.post(`/approvals/${dlg.row.id}/decide`, { decision: dlg.decision, comment });
      toast.success(t("saved")); setDlg(null); setComment(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cols = [
    { key: "task_id", label: "Task", render: (r) => <Link to={`/tasks/${r.task_id}`} className="font-mono text-xs text-sky-700 hover:underline">{r.task_id}</Link> },
    { key: "task_title", label: t("title") },
    { key: "requested_by_name", label: t("requestedBy") },
    { key: "approver_name", label: t("approver"), render: (r) => r.approver_name || "—" },
    { key: "status", label: t("status"), render: (r) => <StatusPill value={r.status} /> },
    { key: "act", label: t("actions"), render: (r) => r.status === "pending" && hasRole(APPROVER_ROLES) ? (
      <div className="flex gap-2">
        <Button size="sm" data-testid={`approve-${r.id}`} onClick={() => setDlg({ row: r, decision: "approved" })} className="rounded-lg bg-emerald-700 hover:bg-emerald-800"><Check className="h-4 w-4" />{t("approve")}</Button>
        <Button size="sm" variant="outline" data-testid={`reject-${r.id}`} onClick={() => setDlg({ row: r, decision: "rejected" })} className="rounded-lg border-rose-300 text-rose-800"><X className="h-4 w-4" />{t("reject")}</Button>
      </div>) : null },
  ];

  return (
    <div>
      <PageHeader title={t("nav_approvals")} sub={`${t("total")}: ${rows.length}`}>
        <FilterSelect value={status} onChange={setStatus} options={["pending", "approved", "rejected"]} placeholder={t("status")} testId="approvals-status-filter" />
      </PageHeader>
      <DataTable cols={cols} rows={rows} testId="approvals-table" />
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="approval-dialog">
          <DialogHeader><DialogTitle>{dlg?.decision === "approved" ? t("approve") : t("reject")} · {dlg?.row.task_id}</DialogTitle></DialogHeader>
          <Textarea data-testid="approval-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("comment")} className="rounded-xl" />
          <DialogFooter><Button data-testid="approval-confirm-button" onClick={decide} className="rounded-xl bg-slate-900">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
